// CookTimerDock — persistent floating timer panel pinned to the top of
// Cook Mode, visible across every step.
//
// What it does:
//   • Shows the current step's parsed durations as "Start Nm" chips
//     (same idea as the old inline StepTimers, but feeding the dock).
//   • Lets the user add an arbitrary custom timer ("+ Add timer" →
//     inline minutes input).
//   • Live countdown for every running timer in this cook session,
//     ticking every second.
//   • Tap a running timer to cancel it (server + native AlarmManager
//     both get the cancel via api.alarmsCancel).
//   • Auto-fades a timer when its fireAt elapses — the OS notification
//     still fires from AlarmManager, the dock just stops tracking it.
//
// Scope: SESSION-LOCAL. The dock only tracks alarms it created this
// cook session. Unrelated active alarms (the user's daily vitamin
// reminder, etc.) don't pollute the cook view. Closing + reopening
// Cook Mode loses tracking but the OS alarm still rings.
//
// State source: in-memory only. We capture the returned Alarm row from
// api.alarmsSet and add it to a local Map. SSE alarm:cancel events
// remove from the map so a cancel from another device or from the
// settings UI also updates this view.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type Alarm } from "../../lib/rpc";
import { sse } from "../../lib/sse";
import { findStepDurations, humanizeSeconds } from "../../lib/step-timers";

interface CookTimerDockProps {
  recipeName: string;
  stepNum: number;
  stepText: string;
}

/** Format the remaining time as MM:SS for <1h, HH:MM:SS otherwise.
 *  Negative remaining (fireAt has passed) clamps to 00:00; the dock
 *  fades the row out shortly after that, so the user won't see the
 *  clamp for long. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function CookTimerDock({ recipeName, stepNum, stepText }: CookTimerDockProps) {
  // Map keyed by alarm.id — easy to splice on cancel + survives row
  // re-renders without losing identity.
  const [timers, setTimers] = useState<Map<string, Alarm>>(() => new Map());
  // Ticks every second so countdowns update. We can't use the alarm
  // table's TTL — the OS handles the firing, the dock just observes.
  const [now, setNow] = useState(() => Date.now());
  const [customOpen, setCustomOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Parse the current step text for durations once per step change.
  // The chip row for "this step's timers" reads from this — taps fire
  // the alarm + thread it into the dock's running set.
  const durations = useMemo(() => findStepDurations(stepText), [stepText]);

  // Per-step "fired chip" ledger. Once the user taps a chip for a
  // parsed duration, we hide the chip — the active timer pill above
  // already shows the countdown, so re-rendering "Start 5m" while a
  // 5m pill is counting down is visual noise. Re-firing the same
  // duration (after it elapses or the user cancels) goes through the
  // "+ Timer" custom input.
  //
  // Keyed on `${stepNum}-${idx}` so navigating between steps doesn't
  // leak hides into other steps.
  const [firedChips, setFiredChips] = useState<Set<string>>(() => new Set());

  const fireTimer = async (seconds: number, labelOverride?: string) => {
    const label = labelOverride || humanizeSeconds(seconds);
    const key = `step-${stepNum}-${seconds}-${Date.now()}`;
    setPending(key);
    try {
      const alarm = await api.alarmsSet({
        name: `${recipeName} — step ${stepNum} (${label})`,
        fireAt: Date.now() + seconds * 1000,
      });
      setTimers((prev) => {
        const next = new Map(prev);
        next.set(alarm.id, alarm);
        return next;
      });
    } catch (err) {
      // Failed to set — silently no-op. Devtools-visible for debug.
      console.warn("cook timer set failed:", err);
    } finally {
      setPending(null);
    }
  };

  const cancelTimer = async (alarmId: string) => {
    // Optimistic remove — the SSE alarm:cancel from the server will
    // reconcile if we missed something.
    setTimers((prev) => {
      const next = new Map(prev);
      next.delete(alarmId);
      return next;
    });
    try { await api.alarmsCancel(alarmId); }
    catch (err) { console.warn("cook timer cancel failed:", err); }
  };

  const submitCustom = async () => {
    const n = Number(customDraft.replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      setCustomDraft("");
      setCustomOpen(false);
      return;
    }
    // Treat the input as MINUTES. 1.5 → 90s. Cap at 24h so a fat-
    // fingered "1000" doesn't accidentally schedule a 16-hour alarm.
    const seconds = Math.min(86400, Math.round(n * 60));
    setCustomDraft("");
    setCustomOpen(false);
    await fireTimer(seconds, `${n}m`);
  };

  // 1Hz tick for the live countdown. Cheaper than rAF; the timestamps
  // we display only have second granularity anyway.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Drop expired timers ~5s after their fireAt — gives the OS
  // notification a beat to surface, then the dock row fades out.
  // (We keep them long enough that a user mid-glance sees "00:00"
  //  flash before disappearance, so it doesn't feel like the timer
  //  was eaten silently.)
  useEffect(() => {
    setTimers((prev) => {
      let dirty = false;
      const next = new Map(prev);
      for (const [id, t] of next) {
        if (t.fireAt < now - 5000) {
          next.delete(id);
          dirty = true;
        }
      }
      return dirty ? next : prev;
    });
  }, [now]);

  // Sync cancellations from other devices / settings UI. We don't
  // listen to alarm:schedule here because the dock only tracks alarms
  // it CREATED — a schedule from a different surface (chat set_alarm,
  // calendar reminder, etc.) shouldn't appear in cook mode.
  useEffect(() => {
    return sse.on((ev: { type?: string; id?: string }) => {
      if (ev?.type !== "alarm:cancel" || !ev.id) return;
      setTimers((prev) => {
        if (!prev.has(ev.id!)) return prev;
        const next = new Map(prev);
        next.delete(ev.id!);
        return next;
      });
    });
  }, []);

  // Sorted: ascending by remaining time (soonest-to-fire first), so
  // the user's attention naturally lands on what's about to ring.
  const sortedTimers = useMemo(
    () => Array.from(timers.values()).sort((a, b) => a.fireAt - b.fireAt),
    [timers],
  );

  // Hide entirely if there's nothing to suggest AND nothing running —
  // saves vertical space on short recipes. Custom-timer entry is
  // always available via the "+" button while the dock is rendered;
  // when fully hidden, the user can re-open it by tapping a step
  // that has a duration phrase. (Most steps with timing language do.)
  const hasAnything = durations.length > 0 || sortedTimers.length > 0;
  if (!hasAnything && !customOpen) {
    return (
      <div className="cook-timer-dock cook-timer-dock-empty">
        <button
          type="button"
          className="cook-timer-add-btn"
          onClick={() => { setCustomOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
          title="Add a custom timer"
        >
          <Icon name="alarm" size={14} />
          <span>Add timer</span>
        </button>
      </div>
    );
  }

  return (
    <div className="cook-timer-dock">
      {/* Active running timers — biggest visual, top of the dock. */}
      {sortedTimers.length > 0 && (
        <div className="cook-timer-active">
          {sortedTimers.map((t) => {
            const remaining = t.fireAt - now;
            const elapsed = remaining <= 0;
            return (
              <button
                key={t.id}
                type="button"
                className={`cook-timer-pill${elapsed ? " is-elapsed" : ""}`}
                onClick={() => cancelTimer(t.id)}
                title="Tap to cancel"
              >
                <span className="cook-timer-time">{formatRemaining(remaining)}</span>
                <span className="cook-timer-name">{t.name.replace(/^[^—]+— /, "")}</span>
                <Icon name="x" size={12} />
              </button>
            );
          })}
        </div>
      )}

      {/* Quick-start chips parsed from the current step + the "+
        * Add timer" button on the right. Same row, wrap on overflow. */}
      <div className="cook-timer-row">
        {durations.map((d, i) => {
          const chipKey = `${stepNum}-${i}`;
          // Hide chips the user already tapped this step — the active
          // pill above is showing the countdown and a redundant
          // "Start 5m timer" chip next to a "04:57" pill is just
          // noise. Re-running the same duration goes through the
          // "+ Timer" custom input.
          if (firedChips.has(chipKey)) return null;
          return (
            <button
              key={chipKey}
              type="button"
              // First (still-unfired) duration parsed from the current
              // step gets the "primary" treatment — accent border so
              // the user can see at a glance which timer the step
              // expects. The user still TAPS to start; we don't
              // auto-fire (an accidental step skim shouldn't schedule
              // a phone alarm).
              className={`cook-timer-chip${i === 0 ? " is-primary" : ""}`}
              disabled={pending !== null}
              onClick={() => {
                setFiredChips((prev) => { const next = new Set(prev); next.add(chipKey); return next; });
                void fireTimer(d.seconds);
              }}
            >
              <Icon name="alarm" size={12} />
              <span>Start {humanizeSeconds(d.seconds)}</span>
            </button>
          );
        })}
        {customOpen ? (
          <div className="cook-timer-custom">
            <input
              ref={inputRef}
              type="number"
              inputMode="decimal"
              min={0.5}
              max={1440}
              step="0.5"
              placeholder="min"
              value={customDraft}
              onInput={(e) => setCustomDraft((e.currentTarget as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); void submitCustom(); }
                else if (e.key === "Escape") { setCustomDraft(""); setCustomOpen(false); }
              }}
              onBlur={() => {
                // Tiny delay so clicking the "Start" button registers
                // BEFORE the blur collapses the input.
                setTimeout(() => {
                  if (!customDraft) setCustomOpen(false);
                }, 100);
              }}
            />
            <button
              type="button"
              className="cook-timer-custom-go"
              onClick={() => void submitCustom()}
              disabled={!customDraft}
            >
              Start
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="cook-timer-add-btn"
            onClick={() => { setCustomOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
            title="Add a custom timer"
          >
            <Icon name="plus" size={12} />
            <span>Timer</span>
          </button>
        )}
      </div>
    </div>
  );
}
